/**
 * Retry logic for failed operations
 */

import { parseError, isRetryableError, logError } from './errorHandler';

/**
 * Retry configuration
 */
export const RETRY_CONFIG = {
  // Default retry settings
  DEFAULT: {
    maxAttempts: 3,
    baseDelay: 1000, // 1 second
    maxDelay: 10000, // 10 seconds
    backoffMultiplier: 2,
    jitter: true
  },
  
  // Network operations
  NETWORK: {
    maxAttempts: 5,
    baseDelay: 500,
    maxDelay: 5000,
    backoffMultiplier: 1.5,
    jitter: true
  },
  
  // Blockchain transactions
  TRANSACTION: {
    maxAttempts: 3,
    baseDelay: 2000,
    maxDelay: 15000,
    backoffMultiplier: 2,
    jitter: false // More predictable for transactions
  },
  
  // API calls
  API: {
    maxAttempts: 4,
    baseDelay: 1000,
    maxDelay: 8000,
    backoffMultiplier: 2,
    jitter: true
  },
  
  // Price fetching (more frequent, shorter delays)
  PRICE_FETCH: {
    maxAttempts: 5,
    baseDelay: 200,
    maxDelay: 2000,
    backoffMultiplier: 1.5,
    jitter: true
  }
};

/**
 * Calculate delay for retry attempt
 */
const calculateDelay = (attempt, config) => {
  let delay = config.baseDelay * Math.pow(config.backoffMultiplier, attempt - 1);
  
  // Apply maximum delay limit
  delay = Math.min(delay, config.maxDelay);
  
  // Add jitter to prevent thundering herd
  if (config.jitter) {
    delay = delay * (0.5 + Math.random() * 0.5);
  }
  
  return Math.floor(delay);
};

/**
 * Sleep utility
 */
const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Retry wrapper for async operations
 */
export const withRetry = async (
  operation,
  config = RETRY_CONFIG.DEFAULT,
  context = {}
) => {
  let lastError;
  
  for (let attempt = 1; attempt <= config.maxAttempts; attempt++) {
    try {
      const result = await operation(attempt);
      
      // Log successful retry if it wasn't the first attempt
      if (attempt > 1) {
        console.log(`✅ Operation succeeded on attempt ${attempt}`, context);
      }
      
      return result;
    } catch (error) {
      lastError = error;
      const parsedError = parseError(error);
      
      // Log the attempt
      logError(error, {
        ...context,
        attempt,
        maxAttempts: config.maxAttempts,
        willRetry: attempt < config.maxAttempts && isRetryableError(parsedError.code)
      });
      
      // Don't retry if it's the last attempt
      if (attempt === config.maxAttempts) {
        break;
      }
      
      // Don't retry if the error is not retryable
      if (!isRetryableError(parsedError.code)) {
        console.log(`❌ Error not retryable: ${parsedError.code}`);
        break;
      }
      
      // Calculate and wait for delay
      const delay = calculateDelay(attempt, config);
      console.log(`⏳ Retrying in ${delay}ms (attempt ${attempt + 1}/${config.maxAttempts})`);
      await sleep(delay);
    }
  }
  
  // All attempts failed
  throw lastError;
};

/**
 * Specialized retry functions for common operations
 */

/**
 * Retry network requests
 */
export const retryNetworkRequest = async (requestFn, context = {}) => {
  return withRetry(
    requestFn,
    RETRY_CONFIG.NETWORK,
    { ...context, type: 'network_request' }
  );
};

/**
 * Retry blockchain transactions
 */
export const retryTransaction = async (transactionFn, context = {}) => {
  return withRetry(
    transactionFn,
    RETRY_CONFIG.TRANSACTION,
    { ...context, type: 'blockchain_transaction' }
  );
};

/**
 * Retry API calls
 */
export const retryApiCall = async (apiCallFn, context = {}) => {
  return withRetry(
    apiCallFn,
    RETRY_CONFIG.API,
    { ...context, type: 'api_call' }
  );
};

/**
 * Retry price fetching
 */
export const retryPriceFetch = async (priceFetchFn, context = {}) => {
  return withRetry(
    priceFetchFn,
    RETRY_CONFIG.PRICE_FETCH,
    { ...context, type: 'price_fetch' }
  );
};

/**
 * Circuit breaker pattern for preventing cascade failures
 */
export class CircuitBreaker {
  constructor(options = {}) {
    this.failureThreshold = options.failureThreshold || 5;
    this.resetTimeout = options.resetTimeout || 60000; // 1 minute
    this.monitoringPeriod = options.monitoringPeriod || 10000; // 10 seconds
    
    this.state = 'CLOSED'; // CLOSED, OPEN, HALF_OPEN
    this.failureCount = 0;
    this.lastFailureTime = null;
    this.successCount = 0;
  }
  
  async execute(operation, context = {}) {
    if (this.state === 'OPEN') {
      if (Date.now() - this.lastFailureTime >= this.resetTimeout) {
        this.state = 'HALF_OPEN';
        this.successCount = 0;
        console.log('🔄 Circuit breaker moving to HALF_OPEN state');
      } else {
        throw new Error('Circuit breaker is OPEN - operation blocked');
      }
    }
    
    try {
      const result = await operation();
      
      if (this.state === 'HALF_OPEN') {
        this.successCount++;
        if (this.successCount >= 3) {
          this.reset();
          console.log('✅ Circuit breaker reset to CLOSED state');
        }
      } else {
        this.reset();
      }
      
      return result;
    } catch (error) {
      this.recordFailure();
      
      if (this.state === 'HALF_OPEN') {
        this.state = 'OPEN';
        this.lastFailureTime = Date.now();
        console.log('❌ Circuit breaker moving to OPEN state from HALF_OPEN');
      }
      
      throw error;
    }
  }
  
  recordFailure() {
    this.failureCount++;
    this.lastFailureTime = Date.now();
    
    if (this.failureCount >= this.failureThreshold) {
      this.state = 'OPEN';
      console.log(`❌ Circuit breaker OPEN - ${this.failureCount} failures`);
    }
  }
  
  reset() {
    this.failureCount = 0;
    this.state = 'CLOSED';
    this.lastFailureTime = null;
    this.successCount = 0;
  }
  
  getState() {
    return {
      state: this.state,
      failureCount: this.failureCount,
      lastFailureTime: this.lastFailureTime,
      successCount: this.successCount
    };
  }
}

/**
 * Global circuit breakers for different services
 */
export const circuitBreakers = {
  jupiter: new CircuitBreaker({ failureThreshold: 3, resetTimeout: 30000 }),
  priceApi: new CircuitBreaker({ failureThreshold: 5, resetTimeout: 60000 }),
  blockchain: new CircuitBreaker({ failureThreshold: 3, resetTimeout: 45000 })
};